/**
 * 더보기. SPEC §4: "휴대폰 녹음 일괄 임포트, 캘린더/주소록 연동, 설정".
 * v1 — 실 캘린더 OAuth + 주소록 sync + 임포트 picker + 로그아웃.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type RecordingFolder, type ScannedFolderCandidate } from '../lib/api';
import { bridge } from '../lib/bridge';
import { PageHeader } from '../components/AppShell';

export function More() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false });
  const { data: creds } = useQuery({
    queryKey: ['calendar-credentials'],
    queryFn: () => api.listCalendarCredentials(),
  });
  const profile = me && 'user_id' in me ? me : null;
  const version = bridge().getAppVersion();

  const googleCred = creds?.items.find((c) => c.provider === 'google' && !c.revoked_at);

  const startCalendarOAuthM = useMutation({
    mutationFn: () => api.startCalendarOAuth('google'),
    onSuccess: (res) => {
      const w = window as unknown as { Butler?: { openCalendarOAuth?: (p: string) => void; openExternalUrl?: (u: string) => void } };
      // Kotlin shell이 있으면 native가 system browser 열기 (P9 안드 작업)
      // 없으면 그냥 같은 탭으로 redirect
      if (w.Butler?.openCalendarOAuth) {
        // Kotlin은 provider 인자만 받음 — server-side에서 OAuth URL 알아서 구성
        w.Butler.openCalendarOAuth('google');
      } else {
        window.location.href = res.authorize_url;
      }
    },
  });

  const revokeM = useMutation({
    mutationFn: (id: string) => api.revokeCalendarCredential(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar-credentials'] }),
  });

  async function onLogout() {
    try { await api.logout(); } catch { /* ignore */ }
    bridge().clearAuthToken();
    navigate('/login', { replace: true });
  }

  // C3 — 오늘 사용량 (메인에 표시)
  const { data: usage } = useQuery({
    queryKey: ['usage-today'],
    queryFn: () => api.usageToday(),
    staleTime: 60_000,
  });

  // H5 — 회원 탈퇴
  const deleteAccountM = useMutation({
    mutationFn: () => api.deleteAccount(),
    onSuccess: () => {
      bridge().clearAuthToken();
      alert('탈퇴 처리되었습니다. 30일 내 영구 삭제됩니다.');
      navigate('/login', { replace: true });
    },
    onError: (err) => {
      alert(`탈퇴 처리 실패: ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  async function onDeleteAccount() {
    const confirmed = window.confirm(
      '정말 탈퇴하시겠어요?\n\n모든 통화·SMS·요약·TODO 데이터가 30일 후 영구 삭제됩니다.\n그 전까지는 운영팀에 문의해 복구 가능합니다.',
    );
    if (!confirmed) return;
    const secondConfirm = window.prompt('탈퇴를 진행하려면 "탈퇴"라고 입력해 주세요.');
    if (secondConfirm?.trim() !== '탈퇴') {
      alert('입력이 정확하지 않아 취소되었습니다.');
      return;
    }
    deleteAccountM.mutate();
  }

  function requestImport() {
    const w = window as unknown as { Butler?: { requestImport?: () => void } };
    if (w.Butler?.requestImport) w.Butler.requestImport();
    else alert('휴대폰 녹음 일괄 임포트는 안드로이드 앱에서만 동작합니다.');
  }

  function syncContacts() {
    const w = window as unknown as { Butler?: { syncContacts?: () => void } };
    if (w.Butler?.syncContacts) {
      w.Butler.syncContacts();
      alert('주소록 동기화 시작됨. 잠시 후 통화·SMS에 이름이 표시됩니다.');
    } else alert('주소록 동기화는 안드로이드 앱에서만 동작합니다.');
  }

  // ---- 통화녹음 폴더 ----
  const { data: foldersResp, refetch: refetchFolders } = useQuery({
    queryKey: ['recording-folders'],
    queryFn: () => api.getRecordingFolders(),
  });
  const savedFolders: RecordingFolder[] = foldersResp?.folders ?? [];
  const [scanning, setScanning] = useState(false);
  const [candidates, setCandidates] = useState<ScannedFolderCandidate[] | null>(null);

  const setFoldersM = useMutation({
    mutationFn: (next: RecordingFolder[]) => api.setRecordingFolders(next),
    onSuccess: () => {
      const native = (window as unknown as {
        Butler?: { setRecordingFolders?: (s: string) => void };
      }).Butler;
      // 안드 native에도 즉시 반영 (FileObserver 재시작 트리거)
      const arr = savedFolders;
      if (native?.setRecordingFolders) {
        try { native.setRecordingFolders(JSON.stringify(arr)); } catch { /* no-op */ }
      }
      qc.invalidateQueries({ queryKey: ['recording-folders'] });
    },
  });

  async function runScan() {
    const native = (window as unknown as {
      Butler?: { scanRecordingFolders?: () => string };
    }).Butler;
    if (!native?.scanRecordingFolders) {
      alert('통화녹음 폴더 탐색은 안드로이드 앱에서만 동작합니다.');
      return;
    }
    setScanning(true);
    try {
      const json = native.scanRecordingFolders();
      const list = JSON.parse(json) as ScannedFolderCandidate[];
      setCandidates(list);
      if (list.length === 0) {
        alert('통화녹음으로 추정되는 폴더를 찾지 못했어요.\n\n통화를 한 번 해보시고 다시 시도해주세요. 또는 권한이 거부됐을 수 있으니 권한을 확인해주세요.');
      }
    } catch (e) {
      alert(`탐색 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setScanning(false);
    }
  }

  function toggleFolder(path: string, enabled: boolean) {
    const next = savedFolders.map((f) => (f.path === path ? { ...f, enabled } : f));
    setFoldersM.mutate(next);
  }
  function removeFolder(path: string) {
    if (!window.confirm(`이 폴더를 제거할까요?\n${path}`)) return;
    const next = savedFolders.filter((f) => f.path !== path);
    setFoldersM.mutate(next);
  }
  function addCandidate(c: ScannedFolderCandidate) {
    if (savedFolders.some((f) => f.path === c.path)) return;
    const next: RecordingFolder[] = [
      ...savedFolders,
      { path: c.path, source: 'auto', enabled: true, discovered_at: new Date().toISOString() },
    ];
    setFoldersM.mutate(next);
    // 후보 리스트에서 추가된 항목 제거
    setCandidates((prev) => (prev ? prev.filter((x) => x.path !== c.path) : prev));
  }
  void refetchFolders;

  return (
    <>
      <PageHeader title="더보기" subtitle="설정과 연동입니다." />
      <main className="px-xl space-y-lg">
        {profile && (
          <section className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md p-lg">
            <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-sm font-semibold">
              계정
            </div>
            <dl className="space-y-xs text-[13px]">
              <div className="flex justify-between gap-md">
                <dt className="text-[var(--text-secondary)]">phone</dt>
                <dd className="font-mono text-[var(--text-primary)]">{profile.phone_e164}</dd>
              </div>
              <div className="flex justify-between gap-md">
                <dt className="text-[var(--text-secondary)]">user_id</dt>
                <dd className="font-mono text-[var(--text-primary)] text-[12px] truncate ml-md max-w-[60%]">
                  {profile.user_id}
                </dd>
              </div>
              <div className="flex justify-between gap-md">
                <dt className="text-[var(--text-secondary)]">가입</dt>
                <dd className="font-mono text-[var(--text-primary)] text-[12px]">
                  {String(profile.created_at).slice(0, 10)}
                </dd>
              </div>
            </dl>
          </section>
        )}

        <section className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md overflow-hidden">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] px-lg pt-lg pb-sm font-semibold">
            프로필
          </div>
          <ul className="divide-y divide-[var(--border)]">
            <ActionRow
              label="온보딩 다시 하기"
              hint="회사 컨텍스트를 다시 알려주면 통화·SMS 분석이 더 정확해집니다."
              onClick={() => navigate('/signup')}
            />
          </ul>
        </section>

        <section className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md overflow-hidden">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] px-lg pt-lg pb-sm font-semibold">
            연동
          </div>
          <ul className="divide-y divide-[var(--border)]">
            {googleCred ? (
              <li className="px-lg py-md">
                <div className="flex items-center justify-between gap-md">
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] text-[var(--text-primary)]">구글 캘린더 연결됨</div>
                    <div className="text-[12px] text-[var(--text-muted)] mt-[2px] truncate">
                      {googleCred.account_email ?? '(이메일 없음)'}
                    </div>
                  </div>
                  <button
                    onClick={() => revokeM.mutate(googleCred.id)}
                    disabled={revokeM.isPending}
                    className="text-[12px] text-[var(--text-secondary)] px-md py-[6px] rounded-md border border-[var(--border)] disabled:opacity-50"
                  >
                    연결 해제
                  </button>
                </div>
              </li>
            ) : (
              <ActionRow
                label="구글 캘린더 연동"
                hint="일정 등록 제안 카드를 받으려면 연결이 필요합니다."
                onClick={() => startCalendarOAuthM.mutate()}
                busy={startCalendarOAuthM.isPending}
              />
            )}
            <ActionRow
              label="휴대폰 녹음 일괄 임포트"
              hint="기존 녹음 폴더를 한 번에 업로드합니다 (안드로이드 앱 전용)."
              onClick={requestImport}
            />
            <ActionRow
              label="주소록 다시 동기화"
              hint="이름이 비어있는 통화·SMS에 표시하기 위해 사용합니다."
              onClick={syncContacts}
            />
            <ActionRow
              label="차단된 연락처"
              hint="특정 연락처의 통화 녹음을 클라우드에 올리지 않도록 차단합니다."
              onClick={() => navigate('/more/blocked')}
            />
          </ul>
        </section>

        <section className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md overflow-hidden">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] px-lg pt-lg pb-sm font-semibold">
            통화녹음 폴더
          </div>
          <div className="px-lg pb-md text-[12px] text-[var(--text-muted)] leading-relaxed">
            기종마다 통화녹음 저장 위치가 달라요. Butler가 자동으로 찾을 폴더를 골라주세요.
          </div>

          {savedFolders.length > 0 && (
            <ul className="divide-y divide-[var(--border)] border-t border-[var(--border)]">
              {savedFolders.map((f) => (
                <li key={f.path} className="px-lg py-md flex items-center gap-md">
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-[var(--text-primary)] font-mono truncate" title={f.path}>
                      {f.path}
                    </div>
                    <div className="text-[11px] text-[var(--text-muted)] mt-[2px]">
                      {f.source === 'auto' ? '자동 발견' : f.source === 'saf' ? '수동 선택' : '직접 추가'}
                      {f.enabled ? '' : ' · 꺼짐'}
                    </div>
                  </div>
                  <label className="inline-flex items-center cursor-pointer gap-xs">
                    <input
                      type="checkbox"
                      checked={f.enabled}
                      onChange={(e) => toggleFolder(f.path, e.target.checked)}
                      className="w-4 h-4 accent-[var(--accent)]"
                    />
                    <span className="text-[12px] text-[var(--text-secondary)]">사용</span>
                  </label>
                  <button
                    onClick={() => removeFolder(f.path)}
                    className="text-[12px] text-[var(--text-muted)] px-sm py-[4px] rounded border border-[var(--border)] hover:text-[var(--text-primary)]"
                    aria-label="제거"
                  >
                    제거
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="px-lg py-md border-t border-[var(--border)] flex flex-col gap-sm">
            <button
              type="button"
              onClick={runScan}
              disabled={scanning || setFoldersM.isPending}
              className="text-[13px] py-[10px] rounded-lg bg-[var(--surface-elev)] text-[var(--text-primary)] border border-[var(--border)] hover:border-[var(--accent)] transition-colors disabled:opacity-50"
            >
              {scanning ? '폴더 탐색 중…' : savedFolders.length > 0 ? '다시 탐색하기' : '내 통화녹음 폴더 찾기'}
            </button>

            {candidates && candidates.length > 0 && (
              <div className="mt-sm space-y-xs">
                <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">
                  발견된 후보 ({candidates.length})
                </div>
                {candidates.map((c) => {
                  const already = savedFolders.some((f) => f.path === c.path);
                  return (
                    <div key={c.path} className="flex items-center gap-sm py-xs">
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-mono text-[var(--text-primary)] truncate" title={c.path}>
                          {c.path}
                        </div>
                        <div className="text-[11px] text-[var(--text-muted)]">
                          {c.confidence === 'high' ? '신뢰도 높음' : c.confidence === 'medium' ? '신뢰도 보통' : '신뢰도 낮음'}
                          {' · '}샘플 {c.sample_count}건
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => addCandidate(c)}
                        disabled={already || setFoldersM.isPending}
                        className="text-[12px] px-sm py-[6px] rounded-md bg-[var(--accent)] text-[var(--accent-on)] disabled:opacity-40"
                      >
                        {already ? '추가됨' : '추가'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {candidates && candidates.length === 0 && (
              <div className="text-[11px] text-[var(--text-muted)] italic">
                후보를 찾지 못했어요. 통화를 한 번 해본 뒤 다시 탐색하거나, 권한 설정을 확인해주세요.
              </div>
            )}
          </div>
        </section>

        {usage && (
          <section className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md p-lg">
            <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-sm font-semibold">
              오늘 AI 사용량
            </div>
            <dl className="space-y-xs text-[13px]">
              <div className="flex justify-between gap-md">
                <dt className="text-[var(--text-secondary)]">호출</dt>
                <dd className="font-mono text-[var(--text-primary)]">
                  {usage.usage.callCount} / {usage.limit.callLimit}회
                </dd>
              </div>
              <div className="flex justify-between gap-md">
                <dt className="text-[var(--text-secondary)]">비용</dt>
                <dd className="font-mono text-[var(--text-primary)]">
                  ₩{usage.usage.costKrw.toFixed(0)} / ₩{usage.limit.krwLimit.toFixed(0)}
                </dd>
              </div>
            </dl>
            <p className="text-[11px] text-[var(--text-muted)] mt-md leading-relaxed">
              한도는 한국 시간 자정에 초기화됩니다.
            </p>
          </section>
        )}

        <section className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md overflow-hidden">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] px-lg pt-lg pb-sm font-semibold">
            법적 고지
          </div>
          <ul className="divide-y divide-[var(--border)]">
            <ActionRow label="이용 약관" onClick={() => window.open('https://butlerai.kr/terms.html', '_blank')} />
            <ActionRow label="개인정보처리방침" onClick={() => window.open('https://butlerai.kr/privacy.html', '_blank')} />
            <ActionRow label="통화녹음 안내" onClick={() => window.open('https://butlerai.kr/call-recording.html', '_blank')} />
          </ul>
        </section>

        <section className="space-y-md">
          <button
            type="button"
            onClick={onLogout}
            className="w-full py-[12px] rounded-lg border border-[var(--border)] bg-[var(--surface-elev)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors text-[14px]"
          >
            로그아웃
          </button>
          <button
            type="button"
            onClick={onDeleteAccount}
            disabled={deleteAccountM.isPending}
            className="w-full py-[12px] rounded-lg border border-red-600/30 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors text-[14px] disabled:opacity-50"
          >
            {deleteAccountM.isPending ? '처리 중…' : '회원 탈퇴'}
          </button>
          <p className="text-center text-[11px] text-[var(--text-muted)] font-mono">
            Butler v{version.versionName} (build {version.versionCode})
          </p>
        </section>
      </main>
    </>
  );
}

function ActionRow({ label, hint, onClick, busy }: { label: string; hint?: string; onClick: () => void; busy?: boolean }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        className="w-full px-lg py-md text-left hover:bg-[var(--surface-elev)] transition-colors disabled:opacity-50"
      >
        <div className="text-[14px] text-[var(--text-primary)]">
          {busy ? '연결 중…' : label}
        </div>
        {hint && <div className="text-[12px] text-[var(--text-muted)] mt-[2px]">{hint}</div>}
      </button>
    </li>
  );
}
